import { useEffect, useState, useRef } from 'react'
import {
  getAccount,
  AccountData,
  PartnerType,
} from '../../modules/partner/action'
import { useDispatch, useSelector } from '../../../store'
import {
  getUserProfile,
  setUserProfile,
} from '../../modules/userProfile/action'
import { errorLogger as newrelicErrLogger } from '../ErrorHandler'
import { useFlagValue, useUpdateConfigFlags } from '@labrav/flags'
import { useAuth0, User } from '@auth0/auth0-react'
import { OodleRum } from '@oodle-ai/rum'
import { AuthProvider } from '../../../oppsync/modules/userList/action'
import { useTracker } from '../Tracker/hook'
import type { Tracker } from '../Tracker/Tracker'
import {
  loadAvailableAccounts,
  loadAvailableAccountsNoCache,
  setSelectedAccount,
  clearAccountSelection,
} from '../../modules/accountSelection'
import { isAdminRole } from '../isAdminRole'
import { hasCachedPartnerData, hasCachedAccountData } from '../partnerDataCache'
import { actionTypeWrapper } from '../actionTypeWrapper'
import { useUserType } from './usePartnerData'
import { useParams } from 'react-router-dom'

// Module-level guard: tracks the last external partnerId that was passed to setConfig.
// Prevents a second flagsClient reset (and resulting flag-fetch stampede) when
// ReferralFormWrapper has already configured the flags client before ReferralForm mounts.
let _lastConfiguredExternalPartnerId: string | undefined

export const markExternalFlagsConfigured = (partnerId: string) => {
  _lastConfiguredExternalPartnerId = partnerId
}

export const isExternalFlagsConfigured = (partnerId: string): boolean =>
  _lastConfiguredExternalPartnerId === partnerId

const identifyUser = (tracker: Tracker | null, user: User) => {
  tracker?.identify(user.sub || '', {
    partner_id: user.partner_id,
    roles: user.auth0_roles,
  })
  tracker?.group(user.partner_id)

  // Local part is masked to its first character; RUM only needs the domain to
  // group users by organisation, never the address itself.
  const [localPart, domain] = (user.email ?? '').split('@')
  OodleRum.identify({
    id: user.sub || '',
    ...(localPart && domain
      ? {
          email: `${localPart[0]}${'*'.repeat(localPart.length - 1)}@${domain}`,
        }
      : {}),
  })
}

export const usePartnerUser = (isExternal = false) => {
  const dispatch = useDispatch()
  const { user } = useAuth0()
  const { value: auth0Enabled } = useFlagValue('auth0')

  const {
    partnerData,
    partnerIdFromExternalPage,
    selectedPartnerIdForAdmin,
    userProfile,
  } = useSelector<{
    partnerData: AccountData | undefined
    partnerIdFromExternalPage: string | undefined
    selectedPartnerIdForAdmin: string | undefined
    userProfile: any
  }>(state => ({
    partnerData: state.AccountData.user.partnerData || undefined,
    partnerIdFromExternalPage:
      state.customForm.hubspotForm.customData?.referralMetadata?.partner_id,
    selectedPartnerIdForAdmin: state.AccountData.admin?.selectedPartnerId,
    userProfile: state.userProfile.userProfile,
  }))

  // Use useUserType hook to determine user type from Redux state (based on user profile roles)
  const partnerType = useUserType()
  const isAdmin = partnerType === PartnerType.Admin

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState()
  const [userDataLoaded, setUserDataLoaded] = useState(false)
  const [hasCachedData, setHasCachedData] = useState(false)
  const [lastSelectedPartnerId, setLastSelectedPartnerId] = useState<
    string | undefined
  >(undefined)
  // Track last selectedAccountIds to detect manual changes (for FLYOUT)
  const lastSelectedAccountIdsRef = useRef<
    Partial<Record<string, Partial<Record<string, string>>>> | undefined
  >(undefined)
  const { setConfig } = useUpdateConfigFlags()
  const tracker = useTracker()
  const { availableAccounts, selectedAccountIds, isLoadingAccounts } =
    useSelector<{
      availableAccounts: any[]
      selectedAccountIds:
        | Partial<Record<string, Partial<Record<string, string>>>>
        | undefined
      isLoadingAccounts: boolean
    }>(state => ({
      availableAccounts: state.accountSelection.availableAccounts || [],
      selectedAccountIds: state.accountSelection.selectedAccountIds || [],
      isLoadingAccounts: state.accountSelection.isLoading || false,
    }))

  // Step 1: Initial setup - load user data
  useEffect(() => {
    const setUp = async () => {
      if (user?.partner_id && !isExternal) {
        performance.mark('partner-hook-start')
        performance.mark('loading-gate:setup-start')

        // Check if we have cached data for blazing fast loading
        const [hasPartnerCache, hasAccountCache] = await Promise.all([
          hasCachedPartnerData(user.partner_id as string),
          !isAdmin
            ? hasCachedAccountData(user.partner_id as string)
            : Promise.resolve(true),
        ])

        const hasBothCaches = hasPartnerCache && hasAccountCache
        setHasCachedData(hasBothCaches)

        if (hasBothCaches) {
          // We have cached data - set up immediately for blazing fast experience
          setUserDataLoaded(true)
          identifyUser(tracker, user)
          setConfig({
            user: {
              userId: user.sub,
              partnerId: user.partner_id as string,
            },
          })
          setLoading(false)
        }

        try {
          const calls: Promise<any>[] = [
            dispatch(getAccount(user.partner_id as string, PartnerType.User)),
            dispatch(getUserProfile(user.partner_id, user.partner_user_id)),
          ]
          if (!isAdmin) {
            calls.push(
              dispatch(loadAvailableAccounts(user.partner_id as string))
            )
          }
          performance.mark('loading-gate:api-calls-start')
          performance.mark('accounts-dispatched')
          await Promise.all(calls)
          performance.mark('accounts-loaded')
          performance.measure(
            'time-to-accounts',
            'partner-hook-start',
            'accounts-loaded'
          )

          if (!hasBothCaches) {
            // Only set up these if we didn't already do it for cached data
            identifyUser(tracker, user)
            setConfig({
              user: {
                userId: user.sub,
                partnerId: user.partner_id as string,
              },
            })

            // Don't set loaded until next tick to ensure Redux state is updated
            setTimeout(() => {
              setUserDataLoaded(true)
            }, 0)
          }
        } catch (err: any) {
          newrelicErrLogger(err)
          setError(err)
        } finally {
          setLoading(false)
        }
      } else if (partnerIdFromExternalPage && isExternal) {
        if (_lastConfiguredExternalPartnerId !== partnerIdFromExternalPage) {
          _lastConfiguredExternalPartnerId = partnerIdFromExternalPage
          setConfig({
            user: {
              partnerId: partnerIdFromExternalPage as string,
            },
          })
        }
        setUserDataLoaded(true)
      }
    }

    setUp()
  }, [auth0Enabled, partnerIdFromExternalPage, user?.partner_id, isExternal])

  // Step 2: After user data is loaded, load available accounts
  useEffect(() => {
    if (!userDataLoaded || isExternal) {
      return
    }

    if (!isAdmin) {
      // Non-admin: accounts already loaded in Step 1 in parallel with getUserProfile
      return
    }

    // everything from here and below is admin-only

    // Capture whether the partner changed BEFORE updating lastSelectedPartnerId.
    // This must happen before the userProfile early-return so that when userProfile
    // loads and this effect re-runs, lastSelectedPartnerId already equals accountPartnerId
    // and we do NOT incorrectly clear accounts that were loaded by the HOC.
    const partnerChanged = lastSelectedPartnerId !== selectedPartnerIdForAdmin
    if (selectedPartnerIdForAdmin && partnerChanged) {
      setLastSelectedPartnerId(selectedPartnerIdForAdmin)
      dispatch(clearAccountSelection())
    }

    if (selectedPartnerIdForAdmin) {
      performance.mark('loading-gate:accounts-call-start')
      console.log('loadAccounts', selectedPartnerIdForAdmin)
      dispatch(loadAvailableAccountsNoCache(selectedPartnerIdForAdmin))
    }
  }, [
    userDataLoaded,
    isAdmin,
    partnerType,
    selectedPartnerIdForAdmin,
    user?.partner_id,
    isExternal,
    userProfile,
  ])

  // Step 3: After available accounts are loaded, auto-select
  // Get route params to check for FLYOUT route-based selection
  const { cloud: routeCloud, accountId: routeAccountId } = useParams<{
    cloud?: string
    accountId?: string
  }>()

  useEffect(() => {
    const shouldAutoSelect =
      !isExternal &&
      userDataLoaded &&
      !isLoadingAccounts && // Don't run while accounts are being fetched
      availableAccounts &&
      availableAccounts.length > 0 &&
      (isAdmin
        ? !!selectedPartnerIdForAdmin && selectedPartnerIdForAdmin.trim() !== ''
        : true)

    if (!shouldAutoSelect) return
    // For admin, verify accounts belong to current selected partner
    if (isAdmin && selectedPartnerIdForAdmin) {
      const firstAccount = availableAccounts[0]
      if (
        firstAccount &&
        firstAccount.partnerId !== selectedPartnerIdForAdmin
      ) {
        return
      }
    }

    const byProduct = availableAccounts.reduce(
      (acc: Record<string, any[]>, a: any) => {
        const key = a.product
        acc[key] = acc[key] || []
        acc[key].push(a)
        return acc
      },
      {}
    )

    Object.entries(byProduct).forEach(([product, accounts]) => {
      if ((accounts as any[]).length > 0) {
        const productUpper = product.toUpperCase()
        const productSelectedAccounts = selectedAccountIds?.[productUpper]
        const previousProductSelectedAccounts =
          lastSelectedAccountIdsRef.current?.[productUpper]

        // For FLYOUT: Check if ANY cloud is already selected (since we only allow one cloud at a time)
        // Also check if route params exist (route-based selection takes precedence)
        // Also check if the selection just changed (manual change detected) - don't override
        // For other products: Check if any cloud is selected to avoid overriding manual selections
        const hasExistingSelection =
          productSelectedAccounts &&
          Object.keys(productSelectedAccounts).length > 0

        // For FLYOUT: If route params exist, don't auto-select (route params will be synced by useSyncFlyoutRouteParams)
        const hasRouteParams =
          productUpper === 'FLYOUT' && routeCloud && routeAccountId

        // Detect if this was a manual change (for FLYOUT, cloud changed)
        const isManualCloudSwap =
          productUpper === 'FLYOUT' &&
          previousProductSelectedAccounts &&
          productSelectedAccounts &&
          Object.keys(previousProductSelectedAccounts).length > 0 &&
          Object.keys(productSelectedAccounts).length > 0 &&
          Object.keys(previousProductSelectedAccounts)[0] !==
            Object.keys(productSelectedAccounts)[0]

        // Detect manual clear: previous had selection but current is empty (for FLYOUT)
        const isManualClear =
          productUpper === 'FLYOUT' &&
          previousProductSelectedAccounts &&
          Object.keys(previousProductSelectedAccounts).length > 0 &&
          (!productSelectedAccounts ||
            Object.keys(productSelectedAccounts).length === 0)

        // Skip auto-select if it's a manual change (cloud swap or clear)
        const isManualChange = isManualCloudSwap || isManualClear

        // Only auto-select if nothing is selected yet AND it's not a manual change AND no route params for FLYOUT
        if (!hasExistingSelection && !isManualChange && !hasRouteParams) {
          const first: any = (accounts as any[])[0]
          const cloud = first?.cloud
          const firstAccountId = first?.id
          if (firstAccountId && cloud) {
            dispatch(setSelectedAccount(firstAccountId, product as any, cloud))
          }
        }
      }
    })

    // Update ref to track current state for next comparison
    lastSelectedAccountIdsRef.current = selectedAccountIds
  }, [
    userDataLoaded,
    isExternal,
    selectedAccountIds,
    availableAccounts,
    isAdmin,
    partnerType,
    selectedPartnerIdForAdmin,
    userProfile,
    isLoadingAccounts,
    routeCloud,
    routeAccountId,
  ])

  return { loading, error, partnerData, hasCachedData }
}
